import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, Link} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {appPaths} from '@/config/appPaths';
import type {AdminAfriexReward} from '@/types';
import {capitalizeWords, formatValues} from '@/utils/dashboard';
import {formatDate} from '@/utils/date';

import UpdateButton from './UpdateButton';

type SummaryProp = {
  data: AdminAfriexReward;
};

const getFieldsToDisplay = (
  data: SummaryProp['data'],
): Record<string, string | undefined>[] => {
  const status = data?.isDeactivated ? 'Inactive' : 'Active';
  return [
    {Name: capitalizeWords(data?.name?.replace(/_/g, ' '))},
    {Description: data.description || 'N/A'},
    {Type: data.type?.replace(/_/g, ' ')?.toUpperCase() || 'N/A'},
    {'Expires On': formatDate(data.ttl as string) || 'N/A'},
    {
      'Points Value':
        (formatValues(data.pointsValue, 'money') || '0') + ' Afriex Points',
    },
    {'Countries Available': data.supportedCountries?.join(', ') || 'All'},
    {Status: capitalizeWords(status)},
  ];
};

const RewardSummary = ({data}: SummaryProp) => {
  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      <div className="flex items-center gap-4 mb-3">
        <h2 className="font-semibold text-xl mr-3">
          {capitalizeWords(data?.name?.replace(/_/g, ' '))} Details
        </h2>
      </div>
      <hr className="border-1 border-gray-200 dark:border-dark-borderMid mt-2 mb-5 w-full" />

      {getFieldsToDisplay(data)?.map((field, idx) => {
        const key = Object.keys(field)[0];
        const value = Object.values(field)[0];

        if (!value || value?.length === 0) {
          return null;
        }

        return (
          <div
            key={idx}
            className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between"
          >
            <p className="text-slate-500 text-sm w-1/3 dark:text-slate-100">
              {key}
            </p>
            <p className="flex w-2/3 text-sm font-medium text-gray-700 overflow-hidden whitespace-normal dark:text-gray-100">
              {value}
            </p>
          </div>
        );
      })}
      <div className="text-sm text-slate-500 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full text-white pt-5 sm:pt-5">
          <div className="col-span-6">
            <div className="flex justify-end gap-4">
              <PermissionsProvider
                permission={AfriexAdminPermission.RewardRead}
              >
                <Link
                  to={`/${appPaths.rewards}/${data.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" colorScheme="cyan">
                    Manage Card
                  </Button>
                </Link>
              </PermissionsProvider>

              <PermissionsProvider
                permission={AfriexAdminPermission.RewardManage}
              >
                <UpdateButton initialData={data} />
              </PermissionsProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardSummary;
